import { Icon } from '@natera/material/lib/icon'
import { NumericFormat } from 'react-number-format'
import type { PrefixCode } from '~/entities/carc-rarc'
import { carcCodes, prefixes, rarcCodes } from '~/entities/carc-rarc'
import { centsToDollars, dollarsToCents } from '~/shared/lib/currency'
import { Button, Chip, MultiSelect, Text, Textfield, Tooltip } from '~/shared/ui'
import type { AmountType } from '../../../../model'
import type { DeductionField, GroupState } from './carc-rarc-group-types'

const PREFIXES_WITH_TOOLTIPS = ['CR', 'CO', 'PI', 'PR', 'UR']
const PR_EXCLUDED_FIELDS: DeductionField[] = ['adjustments', 'nonCovered']

interface CarcRarcGroupFormProps {
  group: GroupState
  deductionField: DeductionField
  amountType: AmountType
  onChange: (group: GroupState) => void
  onDelete: () => void
}

/** All CARC options — number-only codes, shown regardless of prefix. */
const ALL_CARC_OPTIONS = carcCodes
  .map((c) => ({
    label: `${c.code} - ${c.description}`,
    value: String(c.code),
    description: c.description,
    occurrences: 0,
  }))
  .sort((a, b) => Number(a.value) - Number(b.value))

/** All RARC options — any RARC can be assigned to any CARC. */
const ALL_RARC_OPTIONS = rarcCodes
  .map((r) => ({
    label: `${r.code} - ${r.label}`,
    value: r.code,
    description: r.description,
  }))
  .sort((a, b) => a.value.localeCompare(b.value))

/**
 * Single CARC/RARC group form with prefix chips, CARC dropdown,
 * RARC dropdown, payor-specific code, and delete button.
 */
export function CarcRarcGroupForm({
  group,
  deductionField,
  amountType,
  onChange,
  onDelete,
}: CarcRarcGroupFormProps) {
  const { selectedPrefix, selectedCarcCodes, selectedRarcCodes, payorSpecificCode, amount } = group

  const filteredPrefixes = PR_EXCLUDED_FIELDS.includes(deductionField)
    ? prefixes.filter((p) => p.code !== 'PR')
    : prefixes

  function handlePrefixToggle(prefix: PrefixCode) {
    onChange({
      ...group,
      selectedPrefix: selectedPrefix === prefix.code ? null : prefix.code,
    })
  }

  function handleCarcChange(codes: string[]) {
    onChange({ ...group, selectedCarcCodes: codes })
  }

  function handleRarcChange(codes: string[]) {
    onChange({ ...group, selectedRarcCodes: codes })
  }

  function handlePayorCodeChange(value: string) {
    onChange({ ...group, payorSpecificCode: value })
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Prefix filter chips */}
      <div className="flex flex-col gap-2">
        <span>
          <Text appearance="CAPTION">Select prefix</Text>
          <span className="text-error-main">*</span>
        </span>
        <div className="grid grid-cols-2 gap-2">
          {filteredPrefixes.map((prefix) => {
            const chip = (
              <Chip
                key={prefix.code}
                className="w-full"
                size="sm"
                outlined
                selected={selectedPrefix === prefix.code}
                onClick={() => handlePrefixToggle(prefix)}
                rounded
              >
                {prefix.code} &ndash; {prefix.label}
              </Chip>
            )
            return PREFIXES_WITH_TOOLTIPS.includes(prefix.code) ? (
              <Tooltip
                key={prefix.code}
                content={prefix.label}
              >
                <div>{chip}</div>
              </Tooltip>
            ) : (
              chip
            )
          })}
        </div>
      </div>

      {/* CARC selection — all CARCs shown, number-only codes */}
      <MultiSelect
        label="CARC"
        required
        mode="single"
        options={ALL_CARC_OPTIONS}
        value={selectedCarcCodes}
        onValueChange={handleCarcChange}
        placeholder="Select CARC code"
      />

      {/* RARC multi-select */}
      <MultiSelect
        label="RARC"
        options={ALL_RARC_OPTIONS}
        value={selectedRarcCodes}
        onValueChange={handleRarcChange}
        placeholder="Select RARC codes"
        disabled={selectedCarcCodes.length === 0}
      />

      {/* Per-code amount — only for Adjustments & Discounts */}
      {deductionField === 'adjustments' && (
        <div className="relative">
          <Tooltip content="Enter amount for this CARC. Total appears in Adjustments & Discounts.">
            <Icon
              children="info"
              className="text-content-high-emphasis absolute top-[17px] left-4 z-1 cursor-pointer"
            />
          </Tooltip>
          {amountType === 'percentage' ? (
            <NumericFormat
              customInput={Textfield}
              label="Amount"
              leadingIcon={<span />}
              value={amount ?? ''}
              decimalScale={2}
              allowNegative={false}
              isAllowed={({ floatValue }) => floatValue === undefined || floatValue >= 0}
              onValueChange={(values) => onChange({ ...group, amount: values.floatValue ?? null })}
            />
          ) : (
            <NumericFormat
              customInput={Textfield}
              label="Amount"
              leadingIcon={<span />}
              value={centsToDollars(amount) ?? ''}
              thousandSeparator
              decimalScale={2}
              fixedDecimalScale
              allowNegative
              allowedDecimalSeparators={[',', '.']}
              onValueChange={(values) =>
                onChange({ ...group, amount: dollarsToCents(values.floatValue ?? null) })
              }
            />
          )}
        </div>
      )}

      {/* Payor-Specific Code */}
      <div className="relative">
        <Tooltip content="Codes provided by the payor that map to the selected CARC and RARCs">
          <Icon
            children="info"
            className="text-content-high-emphasis absolute top-[17px] left-4 z-1 cursor-pointer"
          />
        </Tooltip>
        <Textfield
          label="Payor-Specific Code"
          leadingIcon={<span />}
          value={payorSpecificCode}
          onChange={(e) => handlePayorCodeChange(e.target.value)}
        />
      </div>

      {/* Delete this group */}
      <div className="flex justify-end">
        <Button
          size="small"
          materialIcon="delete"
          onClick={onDelete}
        >
          Delete
        </Button>
      </div>
    </div>
  )
}
